// Site search: fuzzy (Fuse.js) over the shared catalogue, per-word matching, "under R250" price filter.
// The dialog is built straight away (so typing after "/" is never lost); Fuse.js loads in the
// background once the page is idle, or on first use, whichever comes first.
import { R, esc } from '../lib/format.js';
import { img } from '../lib/media.js';
import { createDialog } from './dialog.js';

export function createSearchIndex(C, Fuse) {
  const ITEMS = C.products.map(p => ({ ...p, typeLabel: p.type === 'ring' ? 'ring rings' : 'necklace necklaces pendant chain' }))
    .concat(C.sets.map(s => ({ ...s, typeLabel: 'set sets bundle gift' })));
  const fuse = new Fuse(ITEMS, {
    keys: [{ name: 'name', weight: 3 }, { name: 'keywords', weight: 2 }, { name: 'meaning', weight: 1.5 }, { name: 'typeLabel', weight: 1 }],
    threshold: 0.34, ignoreLocation: true, minMatchCharLength: 2, includeScore: true,
  });
  const strict = new Fuse(ITEMS, { keys: ['name', 'keywords', 'meaning', 'typeLabel'], threshold: 0.15, ignoreLocation: true, includeScore: true });
  const STOP = new Set(['set', 'sets', 'a', 'an', 'the', 'for', 'my', 'to', 'of', 'and', 'with', 'me', 'her', 'him', 'i', 'in', 'on', 'something', 'some', 'want', 'looking', 'gift', 'gifts', 'present', 'presents', 'idea', 'ideas']);
  return function search(q) {
    q = q.toLowerCase().trim(); if (!q) return [];
    let max = Infinity;
    q = q.replace(/(under|below|less than|<)\s*r?\s*(\d+)/, (_, __, n) => { max = +n; return ' '; });
    const words = q.split(/[^a-z0-9']+/).filter(w => w.length > 1 && !STOP.has(w));
    const pool = ITEMS.filter(i => i.price <= max);
    if (/\b(sale|discount|specials?|deals?|cheap)\b/.test(q)) return pool.filter(i => i.type !== 'set').sort((a, b) => a.price - b.price);
    if (!words.length) return max < Infinity ? pool.filter(i => i.type !== 'set').sort((a, b) => a.price - b.price) : (/\bgift/.test(q) ? C.sets.concat(C.products.filter(p => p.tag === 'best')) : []);
    const score = new Map();
    const add = (res, weight) => res.forEach(r => { if (r.item.price > max) return; const s = score.get(r.item.id) || { item: r.item, hits: 0, sum: 0 }; s.hits += weight; s.sum += (1 - r.score) * weight; score.set(r.item.id, s); });
    if (words.length > 1) add(fuse.search(words.join(' ')), 2);   // whole phrase, e.g. "long distance"
    words.forEach(w => add(w.length <= 4 ? strict.search(w) : fuse.search(w), 1));
    const need = words.length > 2 ? 2 : 1;
    return [...score.values()].filter(s => s.hits >= need).sort((a, b) => b.hits - a.hits || b.sum - a.sum).map(s => s.item);
  };
}

const SUGGEST = ['Rings', 'Necklaces', 'For mom', 'Long distance', 'Spinner', 'Under R250', 'On sale'];

export function mountSearch({ catalog: C, cart, doc = document }) {
  let search = null, loading = null;
  const ready = () => loading || (loading = import('fuse.js').then(({ default: Fuse }) => (search = createSearchIndex(C, Fuse))));
  const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1500));
  if (doc.readyState === 'complete') idle(ready); else addEventListener('load', () => idle(ready));
  const el = doc.createElement('div');
  el.className = 'srch'; el.hidden = true;
  el.innerHTML = `
    <div class="srch-veil" data-close></div>
    <div class="srch-panel" role="dialog" aria-modal="true" aria-label="Search">
      <div class="srch-bar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 5 5"/></svg>
        <input type="search" data-clarity-mask="true" id="srchInput" placeholder="Search pieces or meanings" autocomplete="off" spellcheck="false" aria-label="Search pieces or meanings" aria-controls="srchList" aria-autocomplete="list" role="combobox" aria-expanded="true">
        <button type="button" class="srch-x" data-close aria-label="Close search"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 5l14 14M19 5 5 19"/></svg></button>
      </div>
      <div class="srch-body">
        <div class="srch-chips" id="srchChips"></div>
        <p class="srch-label" id="srchLabel"></p>
        <ul class="srch-list" id="srchList" role="listbox" aria-label="Results"></ul>
        <p class="srch-status" id="srchStatus" aria-live="polite"></p>
      </div>
    </div>`;
  doc.body.appendChild(el);
  const q$ = (s) => el.querySelector(s);
  const input = q$('#srchInput'), list = q$('#srchList'), label = q$('#srchLabel'), status = q$('#srchStatus'), chips = q$('#srchChips');
  chips.innerHTML = SUGGEST.map(s => `<button type="button" class="chip">${s}</button>`).join('');
  let results = [], active = -1, logged = '';

  const row = (it, i) => `<li role="option" id="sr-${i}" class="srch-row${i === active ? ' on' : ''}" aria-selected="${i === active}" data-i="${i}">
      <button type="button" class="srch-open" data-view="${esc(it.id)}">${img(it.img, { sizes: '56px', lazy: false })}<span><b>${esc(it.name)}</b><small>${esc(it.meaning)}</small></span></button>
      <span class="srch-price">${it.was ? `<s>${R(it.was)}</s>` : ''}${R(it.price)}</span>
      ${it.available === false ? '<span class="srch-sold">Sold out</span>' : `<button type="button" class="srch-add" data-sadd="${esc(it.id)}" aria-label="Add ${esc(it.name)} to bag"><svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M7 1v12M1 7h12"/></svg></button>`}
    </li>`;
  function render() {
    const q = input.value.trim();
    if (!q) { results = C.products.filter(p => p.tag === 'best'); label.textContent = 'Best sellers'; status.textContent = ''; chips.hidden = false; }
    else if (!search) { results = []; label.textContent = ''; status.textContent = 'Searching…'; ready().then(render); }
    else { results = search(q); chips.hidden = !!results.length; label.textContent = results.length ? `${results.length} ${results.length === 1 ? 'result' : 'results'}` : ''; status.textContent = results.length ? '' : `No matches for “${q}”. Try a piece, like “ring”, or a feeling, like “peace”.`; }
    active = Math.min(active, results.length - 1);
    list.innerHTML = results.map(row).join('');
    if (active >= 0) input.setAttribute('aria-activedescendant', `sr-${active}`); else input.removeAttribute('aria-activedescendant');
  }
  const logSearch = () => { const q = input.value.trim(); if (q && q !== logged && window.gtag) { window.gtag('event', 'search', { search_term: q }); logged = q; } };
  const dlg = createDialog(el, {
    mode: 'fade', initialFocus: () => input,
    onOpen: () => { input.value = ''; active = -1; render(); },
    onClose: logSearch,
  });
  function view(id) { dlg.close(); location.href = id.startsWith('set-') ? '/#sets' : C.url(id); }
  async function addItem(id) { if (await cart.add(id)) dlg.close(); }

  input.addEventListener('input', () => { active = -1; render(); });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (!results.length) return; active = (active + (e.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length; render(); list.querySelector('.on')?.scrollIntoView({ block: 'nearest' }); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = results[active >= 0 ? active : 0]; if (it) { logSearch(); view(it.id); } }
  });
  el.addEventListener('click', (e) => {
    if (e.target.closest('[data-close]')) return dlg.close();
    const c = e.target.closest('.chip'); if (c) { input.value = c.textContent; active = -1; render(); input.focus(); return; }
    const a = e.target.closest('[data-sadd]'); if (a) { logSearch(); addItem(a.dataset.sadd); return; }
    const v = e.target.closest('[data-view]'); if (v) { logSearch(); view(v.dataset.view); }
  });
  doc.addEventListener('keydown', (e) => {
    if (!el.hidden) return;
    const typing = /INPUT|TEXTAREA|SELECT/.test(doc.activeElement?.tagName);
    if ((e.key === '/' && !typing) || (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey))) { e.preventDefault(); dlg.open(); }
  });
  doc.querySelectorAll('[data-search]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); dlg.open(); }));
  return { open: dlg.open, close: dlg.close, search: async (q) => (await ready())(q) };
}
